import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { Markdown } from '@/components/Markdown'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import ArticleCta from '@/content/ArticleCta'
import { formatDate } from '@/lib/cms/cms'
import { getPublishedPost } from '@/lib/cms/server'
import { pageLinks, pageMeta } from '@/lib/seo'

export const Route = createFileRoute('/blog/$slug')({
  loader: async ({ params }) => {
    const post = await getPublishedPost({ data: { slug: params.slug } })
    if (!post) throw notFound()
    return post
  },
  head: ({ loaderData, params }) =>
    loaderData
      ? {
          meta: pageMeta({
            title: `${loaderData.title} · Masklabs`,
            description: loaderData.description,
            path: `/blog/${params.slug}`,
            type: 'article',
            publishedTime: loaderData.publishedAt,
          }),
          links: pageLinks(`/blog/${params.slug}`),
        }
      : {},
  notFoundComponent: PostNotFound,
  component: Article,
})

function Article() {
  const post = Route.useLoaderData()

  return (
    <main className="mx-auto w-full max-w-3xl px-6 py-16 text-lg">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/">Home</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/blog">Blog</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{post.title}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <article className="mt-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>{formatDate(post.publishedAt)}</span>
          {post.readMinutes ? (
            <>
              <span aria-hidden>·</span>
              <span>{post.readMinutes} min read</span>
            </>
          ) : null}
        </div>
        <h1 className="mt-3 text-4xl font-bold tracking-tight text-balance">
          {post.title}
        </h1>
        {post.author ? (
          <p className="mt-2 text-sm text-muted-foreground">By {post.author}</p>
        ) : null}

        <div className="prose prose-lg prose-neutral mt-8 max-w-none dark:prose-invert">
          <Markdown>{post.bodyMd}</Markdown>
        </div>
      </article>

      <ArticleCta heading="Try the metered model" className="mt-12" />
    </main>
  )
}

function PostNotFound() {
  return (
    <main className="mx-auto flex w-full max-w-3xl flex-col items-start gap-4 px-6 py-24 text-lg">
      <h1 className="text-3xl font-bold tracking-tight">Post not found</h1>
      <p className="text-muted-foreground">
        That article doesn&apos;t exist or may have moved.
      </p>
      <Button asChild>
        <Link to="/blog">Back to the blog</Link>
      </Button>
    </main>
  )
}
